import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import Close from '@diligentcorp/atlas-react-icons/dist/esm/lens/Close.js';

export type ToastSeverity = 'success' | 'error' | 'info' | 'warning';

interface ToastMessage {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number;
}

interface ToastContextValue {
  showToast: (message: string, severity?: ToastSeverity, duration?: number) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = useCallback((message: string, severity: ToastSeverity = 'info', duration = 5000) => {
    const id = Date.now().toString();
    setToast({ id, message, severity, duration });
  }, []);

  const showSuccess = useCallback(
    (message: string) => {
      showToast(message, 'success', 5000);
    },
    [showToast],
  );

  const showError = useCallback(
    (message: string) => {
      showToast(message, 'warning', 8000);
    },
    [showToast],
  );

  const showInfo = useCallback(
    (message: string) => {
      showToast(message, 'info', 5000);
    },
    [showToast],
  );

  const handleClose = () => {
    setToast(null);
  };

  const dismissAction = (
    <Button variant="text" size="medium" onClick={handleClose}>
      <Close aria-label="Dismiss" />
    </Button>
  );

  return (
    <ToastContext.Provider value={{ showToast, showSuccess, showError, showInfo }}>
      {children}
      <Snackbar
        open={!!toast}
        autoHideDuration={toast?.duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={(theme) => ({
          zIndex: theme.zIndex.snackbar,
          top: 'var(--atlas-global-header-height, 64px)',
        })}
      >
        <Alert
          severity={toast?.severity || 'info'}
          action={dismissAction}
          aria-live="polite"
          sx={{ minWidth: 300 }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
